import type { ComponentChildren } from 'preact';

import {
  AutoSyncVisualizationDialogName,
  ColorExtractorDialogName,
  ContextDialogName,
  FacetDialogName,
  SearchBoxDialogName,
  SimpleColorPaletteDialogName,
  VehicleWidgetDialogName,
} from 'Components/names';
import { logRuntimeError } from 'Diagnostics/runtimeLogger';
import type { StoreWidget } from 'Stores/31ecac-ff/config';

interface WidgetLocation {
  selector?: string;
  firstChildOf?: string;
  lastChildOf?: string;
  insertBefore?: string;
  insertAfter?: string;
  replace?: string;
  wrapper?: string;
  class?: string;
  id?: string;
}

interface VisibilityCondition {
  selector?: string;
  urls?: string[];
  ignoreUrls?: string[];
}

// Keep selector-like fields string-only to catch config mistakes early.
function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function isOptionalStringArray(value: unknown): value is string[] | undefined {
  return value === undefined || (Array.isArray(value) && value.every((v) => typeof v === 'string'));
}

const widgetLocationStringFields = [
  'selector',
  'firstChildOf',
  'lastChildOf',
  'insertBefore',
  'insertAfter',
  'replace',
  'wrapper',
  'class',
  'id',
] as const;

function isWidgetLocation(location: WidgetConfig['location']): location is WidgetLocation {
  if (typeof location !== 'object' || location === null) {
    return false;
  }
  const record = location as Record<string, unknown>;
  return widgetLocationStringFields.every((field) => isOptionalString(record[field]));
}

function isVisibilityCondition(visibleIf: WidgetConfig['visibleIf']): visibleIf is VisibilityCondition {
  if (typeof visibleIf !== 'object' || visibleIf === null) {
    return false;
  }
  const record = visibleIf as Record<string, unknown>;
  return (
    isOptionalString(record.selector) &&
    isOptionalStringArray(record.urls) &&
    isOptionalStringArray(record.ignoreUrls)
  );
}

export interface WidgetConfig extends Omit<StoreWidget, 'location'> {
  type: string;
  location?: string | WidgetLocation;
  element?: Element | null;
  visible?: boolean;
  isRoot?: boolean;
  hasFixedPosition?: boolean;
  hasSearchResults?: boolean;
  needInitRequest?: boolean;
  consideredInScroll?: boolean;
  noSearchResults?: boolean;
  component?: () => ComponentChildren;
  visibleIf?: (() => boolean) | VisibilityCondition;
}

const widgetProcessors = [
  widgetTypeResolve,
  widgetIsRootResolve,
  widgetVisibleResolve,
  widgetHasSearchResultResolve,
  widgetNeedInitRequestResolve,
  widgetConsideredInScrollResolve,
];

export default function processWidgets(widgets: WidgetConfig[]): WidgetConfig[] {
  const widgetsResolved = widgets.map((widget) => widgetProcessors.reduce((w, proc) => proc(w), widget));

  const createWidgetName = (widget: WidgetConfig): string =>
    typeof widget.name === 'string' && widget.name
      ? widget.name
      : `${widget.type}:${typeof widget.template === 'string' ? widget.template : ''}`;

  for (let i = 0; i < widgetsResolved.length; i++) {
    for (let j = 0; j < widgetsResolved.length; j++) {
      if (i === j) {
        continue;
      }
      if (
        widgetsResolved[i].visible &&
        widgetsResolved[j].visible &&
        widgetsResolved[i].element &&
        widgetsResolved[i].element === widgetsResolved[j].element
      ) {
        console.warn(
          `${createWidgetName(widgetsResolved[i])} widget uses the same render element ` +
            `as ${createWidgetName(widgetsResolved[j])} widget; ` +
            `element is ${String(widgetsResolved[j].element)}; ` +
            'to resolve it use the same name, or different IDs',
          widgetsResolved[i],
          widgetsResolved[j],
        );
      }
    }
  }

  return widgetsResolved;
}

function widgetTypeResolve(widget: WidgetConfig): WidgetConfig {
  return { ...widget, type: widget.type ?? widget.name };
}

function widgetIsRootResolve(widget: WidgetConfig): WidgetConfig {
  const hasFixedPosition = [
    'Dialog',
    ContextDialogName,
    FacetDialogName,
    SearchBoxDialogName,
    VehicleWidgetDialogName,
    SimpleColorPaletteDialogName,
    ColorExtractorDialogName,
    AutoSyncVisualizationDialogName,
  ].includes(widget.type);
  const isRoot = !!(widget.template && (widget.location || hasFixedPosition));
  return { ...widget, hasFixedPosition, isRoot };
}

// `querySelector<HTMLElement>` is not a runtime guarantee.
function asHtmlElement(value: Element | null): HTMLElement | null {
  return value instanceof HTMLElement ? value : null;
}

function widgetVisibleResolve(widget: WidgetConfig): WidgetConfig {
  let element: HTMLElement | null = null;
  let shouldClearSelectorContent = false;

  if (!widget.name) {
    const message = `The widget must have \`name\`${
      widget.hasFixedPosition
        ? ' and `template`.'
        : !widget.isRoot
          ? '. If you want the widget to be a root one, add `location` & `template`.'
          : '.'
    }`;
    console.warn(message, widget);
  }

  const isVisible = isWidgetVisible(widget);

  if (isVisible) {
    if (widget.hasFixedPosition) {
      const fixedLocation = isWidgetLocation(widget.location) ? widget.location : undefined;
      element = document.createElement('DIV');
      if (widget.type.includes('Dialog')) {
        // can't use classList.add() with multiple arguments because IE doesn't support it
        ['cm_dialog', widget.name, widget.type === 'Dialog' && 'cm_hide']
          .concat(fixedLocation?.class?.split(' '))
          .filter((c): c is string => Boolean(c))
          .forEach((c) => element?.classList.add(c));
      }

      const customId = fixedLocation?.id;
      element?.setAttribute('id', customId ? customId : `cm_${widget.name}`);

      if ('body' in document) {
        document.body.appendChild(element);
      }
    } else if (typeof widget.location === 'string') {
      element = asHtmlElement(document.querySelector(widget.location));
      shouldClearSelectorContent = true;
    } else if (isWidgetLocation(widget.location)) {
      const { location } = widget;
      const wrapper = document.createElement((location.wrapper || 'div').toUpperCase());
      wrapper.id = `cm_${widget.name}`;

      if (location.selector) {
        element = asHtmlElement(document.querySelector(location.selector));
        shouldClearSelectorContent = true;
      } else if (location.firstChildOf) {
        const parentElement = document.querySelector(location.firstChildOf);
        if (parentElement) {
          element = wrapper;
          parentElement.insertBefore(element, parentElement.firstChild);
        }
      } else if (location.lastChildOf) {
        const parentElement = document.querySelector(location.lastChildOf);
        if (parentElement) {
          element = wrapper;
          parentElement.appendChild(element);
        }
      } else if (location.insertBefore) {
        const nextSibling = document.querySelector(location.insertBefore);
        const { parentElement } = nextSibling || {};
        if (nextSibling && parentElement) {
          element = wrapper;
          parentElement.insertBefore(element, nextSibling);
        }
      } else if (location.replace) {
        const replace = document.querySelector(location.replace);
        const { parentElement } = replace || {};
        if (replace && parentElement) {
          element = wrapper;
          parentElement.replaceChild(element, replace);
        }
      } else if (location.insertAfter) {
        const referenceNode = document.querySelector(location.insertAfter);
        if (referenceNode) {
          element = wrapper;
          referenceNode.parentNode?.insertBefore(element, referenceNode.nextSibling);
        }
      }

      if (element) {
        if (location.class) {
          element.classList.add(...location.class.split(' '));
        }
        if (location.id) {
          element.id = location.id;
        }
      }
    }

    if (element && shouldClearSelectorContent) {
      element.replaceChildren();
    }
  }

  return { ...widget, element, visible: isVisible && !(widget.isRoot && !element) };
}

function isWidgetVisible(widget: WidgetConfig): boolean {
  if (widget.visibleIf) {
    if (typeof widget.visibleIf === 'function') {
      let result: boolean;
      try {
        result = widget.visibleIf();
      } catch (error) {
        logRuntimeError({
          source: 'widgetProcessor:visibleIf',
          error,
          extra: { 'widget.name': String(widget.name ?? widget.type) },
        });
        return false;
      }
      if (!result) {
        return false;
      }
    }
    const { selector, urls, ignoreUrls } = isVisibilityCondition(widget.visibleIf) ? widget.visibleIf : {};
    const checkUrls = (urlsArr: string[]): boolean => urlsArr.some((u) => location.pathname === u);

    const selectorMatch = !selector || !!document.querySelector(selector);
    const urlsMatch = !urls || checkUrls(urls);
    const ignoreUrlsMatch = ignoreUrls && checkUrls(ignoreUrls);

    return selectorMatch && urlsMatch && !ignoreUrlsMatch;
  }
  return true;
}

function widgetHasSearchResultResolve(widget: WidgetConfig): WidgetConfig {
  let hasSearchResults = false;
  switch (widget.type) {
    case 'SearchResult':
      hasSearchResults = !!widget.element;
      break;
    case 'SearchPage':
      hasSearchResults = !widget.noSearchResults;
      delete widget.noSearchResults;
      break;
    case 'Dialog':
      hasSearchResults = !!widget.hasSearchResults;
      break;
  }
  return { ...widget, hasSearchResults };
}

function widgetNeedInitRequestResolve(widget: WidgetConfig): WidgetConfig {
  // TODO: revisit this initialization heuristic.
  const visible = widget.element && widget.visible;
  let needInitRequest = false;
  switch (widget.type) {
    case 'Dialog':
    case 'SearchPage':
      needInitRequest = !!(visible && (widget.needInitRequest || widget.hasSearchResults));
      break;
    case 'SearchResult':
    case 'FacetPanel':
      needInitRequest = !!(visible && widget.needInitRequest);
      break;
  }
  return { ...widget, needInitRequest };
}

function widgetConsideredInScrollResolve(widget: WidgetConfig): WidgetConfig {
  let consideredInScroll = false;
  switch (widget.type) {
    case 'FacetPanel':
    case 'SearchPage':
      // consider only root widgets visible on this page
      consideredInScroll = !!(widget.visible && widget.element && widget.isRoot);
      break;
  }
  return { ...widget, consideredInScroll };
}
